import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  X, Briefcase, Receipt, Users, Clock, DollarSign, Car, MapPin,
  Calendar as CalendarIcon, Package, ChevronDown, ChevronUp,
} from "lucide-react";
import { mainMeta, subMeta } from "../lib/pipeline";
import { FILLER_THRESHOLD } from "./PaintDepthPanel";

/**
 * Contact > Job > Job-info view. Fullscreen portal that loads a contact's
 * entire lifecycle: every job they've had, plus the invoices, installers,
 * time-taken, and workshop journey for each.
 */
export default function ContactJobHistory({ contactId, onClose }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState("job"); // "job" | "vehicle"

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    api.get(`/contacts/${contactId}/summary`)
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err.response?.data?.detail || "Failed to load contact history"))
      .finally(() => setLoading(false));
  }, [contactId]);

  if (!contactId) return null;

  const contact = data?.contact;
  const jobs = data?.jobs || [];
  const totals = data?.totals || {};

  return createPortal(
    <div
      data-testid="contact-history-modal"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-strong rounded-3xl w-full max-w-4xl my-8 overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-start justify-between gap-4">
          <div>
            <div className="text-label flex items-center gap-1"><Users className="w-3 h-3" /> Contact history</div>
            <h2 className="font-heading text-3xl text-white mt-1" data-testid="contact-history-name">
              {contact?.name || (loading ? "Loading…" : "Contact")}
            </h2>
            {contact && (
              <div className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {contact.email && <span>{contact.email}</span>}
                {contact.phone && <span>{contact.phone}</span>}
                {contact.vehicle && <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {contact.vehicle}</span>}
              </div>
            )}
          </div>
          <button
            data-testid="contact-history-close"
            onClick={onClose}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Totals strip */}
        {!loading && data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-6 border-b border-white/5">
            <Stat icon={Briefcase} label="Total jobs" value={totals.jobs_count} />
            <Stat icon={Clock} label="Active" value={totals.active_count} />
            <Stat icon={Receipt} label="Invoices" value={totals.invoices_count} />
            <Stat icon={DollarSign} label="Paid revenue" value={`$${Number(totals.revenue_paid || 0).toLocaleString()}`} />
          </div>
        )}

        {/* Jobs list */}
        <div className="p-6 space-y-3 max-h-[65vh] overflow-y-auto">
          {!loading && jobs.length > 0 && (
            <div className="flex items-center justify-between gap-2 mb-1" data-testid="history-groupby-toggle">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                {groupBy === "job" ? "Grouped by job" : "Grouped by vehicle"}
              </div>
              <div className="inline-flex bg-white/5 border border-white/10 rounded-full p-0.5">
                {["job", "vehicle"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => { setGroupBy(k); setExpanded(null); }}
                    data-testid={`history-groupby-${k}`}
                    className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-widest transition ${groupBy === k ? "bg-white text-black" : "text-slate-400 hover:text-white"}`}
                  >
                    By {k}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center py-12 text-slate-500 italic">Loading…</div>
          )}
          {!loading && jobs.length === 0 && (
            <div className="text-center py-12 text-slate-500 italic" data-testid="contact-history-empty">
              No jobs yet for this contact.
            </div>
          )}
          {!loading && groupBy === "vehicle" && jobs.length > 0 && (
            <VehicleGroupedJobs
              jobs={jobs}
              expanded={expanded}
              setExpanded={setExpanded}
            />
          )}
          {!loading && groupBy === "job" && jobs.map((j) => {
            const isOpen = expanded === j.id;
            const main = mainMeta(j.main_status);
            const sub = subMeta(j.main_status, j.sub_status);
            return (
              <div
                key={j.id}
                data-testid={`contact-history-job-${j.id}`}
                className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : j.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`pill pill-${main.colour} text-[10px]`}>
                        {React.createElement(main.icon, { className: "w-2.5 h-2.5" })}
                        {main.label}
                      </span>
                      <span className="pill pill-muted text-[10px]">
                        {React.createElement(sub.icon, { className: "w-2.5 h-2.5" })}
                        {sub.label}
                      </span>
                      <span className="text-sm text-white truncate">{j.vehicle || "—"}</span>
                      <span className="text-xs text-slate-400 truncate">· {j.service || "—"}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      Started {j.created_at?.slice(0, 10) || "—"}
                      {j.job_ended_at && ` · Archived ${j.job_ended_at.slice(0, 10)}`}
                      {j.amount > 0 && ` · $${Number(j.amount).toLocaleString()}`}
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>

                {isOpen && (
                  <div className="border-t border-white/5 p-4 space-y-3 bg-black/20" data-testid={`contact-history-job-details-${j.id}`}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <Detail icon={CalendarIcon} label="Scheduled" value={j.scheduled_date ? `${j.scheduled_date}${j.scheduled_end_date && j.scheduled_end_date !== j.scheduled_date ? ` → ${j.scheduled_end_date}` : ""}` : "—"} />
                      <Detail icon={MapPin} label="Location" value={j.location_type === "off_site" ? (j.location_address || "Off-site") : "On-site (shop)"} />
                      <Detail icon={Clock} label="Time in workshop" value={timeTaken(j)} />
                    </div>

                    {/* Installers */}
                    {j.installers?.length > 0 && (
                      <div>
                        <div className="text-label mb-1.5 flex items-center gap-1"><Users className="w-3 h-3" /> Installers</div>
                        <div className="flex flex-wrap gap-1.5">
                          {j.installers.map((i) => (
                            <span key={i.id} className="pill pill-info text-[10px]">{i.name}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Paint depth */}
                    {(j.ptg_readings || []).length > 0 && (
                      <div>
                        <div className="text-label mb-1.5">Paint depth (PTG)</div>
                        <div className="flex flex-wrap gap-1.5">
                          {j.ptg_readings.map((r) => {
                            const flag = (r.value_um || 0) >= FILLER_THRESHOLD;
                            return (
                              <span
                                key={r.id}
                                data-testid={`history-ptg-${r.id}`}
                                className={`pill text-[10px] ${flag ? "pill-warning" : "pill-muted"}`}
                                title={r.note || ""}
                              >
                                {r.panel}: {r.value_um} µm
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Materials */}
                    {j.materials?.length > 0 && (
                      <div>
                        <div className="text-label mb-1.5 flex items-center gap-1"><Package className="w-3 h-3" /> Materials used</div>
                        <div className="flex flex-wrap gap-1.5">
                          {j.materials.map((m, i) => (
                            <span key={i} className="pill pill-muted text-[10px]">{m}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Invoices */}
                    <div>
                      <div className="text-label mb-1.5 flex items-center gap-1"><Receipt className="w-3 h-3" /> Invoices</div>
                      {(j.invoices || []).length === 0 ? (
                        <div className="text-xs text-slate-500 italic">No invoice raised yet.</div>
                      ) : (
                        <div className="space-y-1.5">
                          {j.invoices.map((iv) => (
                            <div key={iv.id} className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2 text-sm" data-testid={`contact-history-invoice-${iv.id}`}>
                              <div className="text-white font-medium">${Number(iv.amount).toLocaleString()}</div>
                              <span className={`pill text-[10px] ${iv.status === "paid" ? "pill-success" : iv.status === "overdue" ? "pill-error" : "pill-warning"}`}>{iv.status}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Status history timeline */}
                    {j.status_history?.length > 0 && (
                      <div>
                        <div className="text-label mb-1.5">Journey</div>
                        <div className="space-y-1">
                          {j.status_history.map((h, i) => (
                            <div key={i} className="text-[11px] text-slate-400 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                              <span className="text-slate-300">{subMeta(h.main_status, h.sub_status).label}</span>
                              <span className="opacity-60">· {h.at?.slice(0, 16).replace("T", " ") || ""}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {j.notes && (
                      <div className="text-xs text-slate-400 whitespace-pre-wrap pt-2 border-t border-white/5">
                        {j.notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
      <div className="text-label flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-heading text-2xl text-white mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
      <div className="text-label flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="text-sm text-white mt-1 break-words">{value || "—"}</div>
    </div>
  );
}

/** Human-friendly duration from job_started_at → job_ended_at (or now). */
function timeTaken(job) {
  const start = job.job_started_at ? new Date(job.job_started_at) : null;
  const end = job.job_ended_at ? new Date(job.job_ended_at) : (job.main_status === "job" ? new Date() : null);
  if (!start || !end) return "—";
  const ms = end - start;
  if (ms <= 0) return "—";
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((ms % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

/**
 * Groups the same customer's jobs by vehicle (year/make/model or the legacy
 * `vehicle` string). Cars that appear across multiple jobs show all their
 * bookings under a single header — the receptionist can see at a glance
 * how many times the shop has worked on that specific vehicle.
 */
function VehicleGroupedJobs({ jobs, expanded, setExpanded }) {
  const buckets = new Map();
  jobs.forEach((j) => {
    const items = j.job_items && j.job_items.length > 0
      ? j.job_items
      : [{ id: `legacy-${j.id}`, year: "", make: "", model: "", notes: j.vehicle || "" }];
    items.forEach((it) => {
      const key = [it.year, it.make, it.model].filter(Boolean).join(" ").trim()
        || (it.notes || j.vehicle || "Unknown vehicle").split("\n")[0];
      const arr = buckets.get(key) || [];
      arr.push({ job: j, item: it });
      buckets.set(key, arr);
    });
  });
  const sorted = Array.from(buckets.entries()).sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="space-y-3" data-testid="history-by-vehicle">
      {sorted.map(([vehicle, entries]) => {
        const isOpen = expanded === `veh-${vehicle}`;
        return (
          <div
            key={vehicle}
            data-testid={`history-vehicle-${vehicle.replace(/\s+/g, "-").toLowerCase()}`}
            className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : `veh-${vehicle}`)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03]"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{vehicle}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {entries.length} {entries.length === 1 ? "visit" : "visits"}
                </div>
              </div>
              {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {isOpen && (
              <div className="border-t border-white/5 p-4 space-y-2 bg-black/20">
                {entries.map(({ job: j, item }, i) => (
                  <div key={`${j.id}-${item.id || i}`} className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="text-white truncate">{j.service || item.notes || j.vehicle || "—"}</div>
                      <div className="text-[11px] text-slate-500">
                        {j.created_at?.slice(0, 10) || "—"}
                        {j.main_status ? ` · ${j.main_status}` : ""}
                        {j.sub_status ? ` · ${j.sub_status.replace(/_/g, " ")}` : ""}
                      </div>
                    </div>
                    {j.amount > 0 && <span className="text-slate-300 text-xs">${Number(j.amount).toLocaleString()}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
